import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useContext, useCallback, useEffect } from 'react';
import { Box, Avatar, Typography, TextField, InputAdornment } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { IconContext, useApi, API } from '@plumeuk/shapeshift-identity';
import 'x2js';

const CrownIcon = () => {
    return (jsx("svg", { width: "18", height: "14", viewBox: "0 0 18 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { d: "M16.0554 13L16.9942 4.45072C17.0086 4.33443 16.9961 4.21562 16.9582 4.10727C16.9202 3.99892 16.8582 3.90521 16.7789 3.83637C16.6996 3.76754 16.6061 3.72623 16.5086 3.71697C16.4111 3.70771 16.3133 3.73086 16.226 3.78387L13.182 6.63933C13.1252 6.69616 13.059 6.73795 12.9879 6.762C12.9167 6.78605 12.8421 6.79181 12.769 6.7789C12.6959 6.766 12.6258 6.73472 12.5634 6.6871C12.501 6.63949 12.4476 6.5766 12.4068 6.50254L9.39829 1.21909C9.34888 1.15033 9.28773 1.09518 9.219 1.05737C9.15028 1.01957 9.07558 1 9 1C8.92442 1 8.84972 1.01957 8.781 1.05737C8.71227 1.09518 8.65112 1.15033 8.60171 1.21909L5.59323 6.50254C5.55239 6.5766 5.49902 6.63949 5.43661 6.6871C5.3742 6.73472 5.30415 6.766 5.23101 6.7789C5.15787 6.79181 5.0833 6.78605 5.01214 6.762C4.94097 6.73795 4.87482 6.69616 4.818 6.63933L1.77395 3.85227C1.68665 3.79925 1.58891 3.77611 1.49141 3.78537C1.39391 3.79463 1.3004 3.83593 1.22111 3.90477C1.14182 3.9736 1.0798 4.06732 1.04183 4.17567C1.00386 4.28402 0.991409 4.40283 1.00583 4.51911L1.94465 13H16.0554Z", fill: "#F96E46", stroke: "#F96E46", strokeWidth: "1.2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
};

const getInitials = (name) => name.split(" ").slice(0, 2).map(str => str.slice(0, 1)).join("").toUpperCase();
const getFirstName = (name) => name.split(" ").slice(0, 1)[0];

const getTimeFromNow = (dateStr) => {
    const date = new Date(dateStr);
    const currentDate = new Date();
    const diffInMs = currentDate.getTime() - date.getTime();
    const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));
    if (diffInDays === 0) {
        return "Today";
    }
    else if (diffInDays === 1) {
        return "Yesterday";
    }
    else if (diffInDays <= 7) {
        return `${diffInDays} days ago`;
    }
    else {
        return date.toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "numeric" });
    }
};

const useStyles$3 = makeStyles()(() => ({
    container: {
        width: "40px",
        marginLeft: "24px",
        borderLeft: "1px solid #D8D8D8",
        position: "relative"
    },
    horizontalLine: {
        height: "1px",
        width: "40px",
        backgroundColor: "#D8D8D8",
        position: "absolute",
        top: "24px"
    }
}));
const CommentIndent = ({ className, ...props }) => {
    const { classes, cx } = useStyles$3();
    return (jsx(Box, { className: cx(classes.container, className), ...props, children: jsx("div", { className: classes.horizontalLine }) }));
};

const useStyles$2 = makeStyles()((_, { authorType }) => ({
    container: {
        display: "flex",
        flexDirection: "row"
    },
    avatarContainer: {
        display: "flex",
        flexDirection: "column",
        position: "relative"
    },
    avatar: {
        backgroundColor: "#2A2A2F",
        width: "48px",
        height: "48px"
    },
    authorContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        marginBottom: "6px"
    },
    author: {
        fontSize: "13px",
        fontWeight: "700",
        color: authorType === "instructor" ? "#F96E46" : "#2A2A2F",
        marginLeft: authorType === "instructor" ? "8px" : "0px"
    },
    time: {
        fontSize: "13px",
        fontWeight: "400",
        color: "#A8A8A8",
        marginLeft: "8px"
    },
    contentContainer: {
        marginLeft: "16px",
        display: "flex",
        flexDirection: "column",
        flex: "1"
    },
    content: {
        fontSize: "16px",
        fontWeight: "400",
        color: "#2A2A2F",
        marginBottom: "28px"
    },
    verticalLine: {
        width: "1px",
        backgroundColor: "#D8D8D8",
        flex: "1",
        marginLeft: "24px"
    }
}));
const CommentCard = ({ comment, isChild = false, authorType = "user", ...props }) => {
    const { author: { name, avatar }, createdAt, content, isAdminComment } = comment;
    const { classes } = useStyles$2({ authorType });
    return (jsxs(Box, { className: classes.container, ...props, children: [isChild && jsx(CommentIndent, {}), jsxs(Box, { className: classes.avatarContainer, children: [jsx(Avatar, { src: avatar, className: classes.avatar, children: isAdminComment ? "M" : getInitials(name) }), !isChild && jsx("div", { className: classes.verticalLine })] }), jsxs(Box, { className: classes.contentContainer, children: [jsxs(Box, { className: classes.authorContainer, children: [authorType === "instructor" && jsx(CrownIcon, {}), jsx(Typography, { className: classes.author, children: isAdminComment ? "Moderator" : name }), jsx(Typography, { className: classes.time, children: getTimeFromNow(createdAt) })] }), jsx(Typography, { className: classes.content, children: content })] })] }));
};

const useStyles$1 = makeStyles()((_, { commentType }) => ({
    input: {
        width: commentType === "comment" ? "100%" : "400px",
        border: "1px solid #d8d8d8",
        borderRadius: "6px",
        padding: commentType === "comment" ? "10px 16px" : "8px 16px",
        boxSizing: "border-box"
    },
    icon: {
        color: "#d8d8d8",
        cursor: "pointer"
    },
    attachIcon: {
        marginRight: "18px",
        verticalAlign: "middle",
        display: "none"
    },
    // TODO: we need to figure out what to do with uploading comment attachments and how to handle those
    // See this card: https://plume.eu.teamwork.com/app/tasks/20003997
    hiddenFileUploadField: {
        display: "none"
    },
    selectedFile: {
        verticalAlign: "middle",
        marginRight: "10px"
    }
}));
const CommentInput = ({ postComment, commentType = "comment", threadOf, placeholder = "Leave a comment", className, ...props }) => {
    const { classes, cx } = useStyles$1({ commentType });
    const [comment, setComment] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const { icon } = useContext(IconContext);
    const handleChange = useCallback((event) => {
        setComment(event.target.value);
    }, [setComment]);
    const handleSubmit = useCallback(() => {
        if (!comment)
            return;
        const commentToPost = {
            content: comment
        };
        if (threadOf)
            commentToPost.threadOf = threadOf;
        postComment(commentToPost);
        setComment("");
    }, [comment, setComment, postComment, threadOf]);
    const handleFileChange = useCallback((event) => {
        if (event.target.files && event.target.files.length > 0) {
            setSelectedFile(event.target.files[0]);
        }
    }, [setSelectedFile]);
    return (jsx(TextField, { className: cx(classes.input, className), placeholder: placeholder, variant: "standard", onKeyDown: e => {
            if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
            }
        }, multiline: true, InputProps: {
            disableUnderline: true,
            autoCapitalize: "none",
            autoComplete: "off",
            endAdornment: (jsxs(InputAdornment, { position: "end", children: [jsxs("label", { htmlFor: "file-input", children: [selectedFile &&
                                jsxs("label", { className: classes.selectedFile, children: ["File selected: ", selectedFile.name] }), icon("attachFileOutlinedIcon", { className: cx(classes.icon, classes.attachIcon) })] }), jsx("input", { id: "file-input", type: "file", "data-test-id": `comment-input${(threadOf ? `-thread-${threadOf}` : "")}-${commentType}`, accept: ".jpg,.jpeg,.png", className: classes.hiddenFileUploadField, onChange: handleFileChange }), icon("sendOutlinedIcon", {
                        "data-test-id": "comment-input-send-btn",
                        className: classes.icon,
                        onClick: handleSubmit
                    })] }))
        }, onChange: handleChange, value: comment, ...props }));
};

const useStyles = makeStyles()(() => ({
    container: {
        backgroundColor: "white",
        borderRadius: "10px",
        padding: "25px",
        marginTop: "25px"
    },
    replyContainer: {
        display: "flex",
        flexDirection: "row"
    }
}));
const CommentThread = ({ comment, postComment, className, ...props }) => {
    const { classes, cx } = useStyles();
    return (jsxs(Box, { className: cx(classes.container, className), ...props, children: [jsx(CommentCard, { comment: comment }), comment.children.map(child => jsx(CommentCard, { isChild: true, comment: child }, child.id)), jsxs(Box, { className: classes.replyContainer, children: [jsx(CommentIndent, { sx: { height: "24px" } }), jsx(CommentInput, { threadOf: comment.id, postComment: comment => postComment(comment), commentType: "reply", placeholder: `Reply to ${getFirstName(comment.author.name)}` })] })] }));
};

const useComments = (courseSlug, moduleType, moduleSlug, data) => {
    const [apiResult, getData, setComments] = useApi((data || !courseSlug || !moduleSlug || !moduleType) ? null : API.comments(courseSlug, moduleType, moduleSlug), data);
    const getComments = (courseSlugOverride, typeOverride, moduleSlugOverride, configOverride) => {
        const _type = typeOverride ?? moduleType;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        const _moduleSlug = moduleSlugOverride ?? moduleSlug;
        if (!_courseSlug || !_moduleSlug || !_type)
            return;
        const url = API.comments(_courseSlug, _type, _moduleSlug);
        getData(configOverride ? { ...configOverride, url } : url);
    };
    useEffect(() => data && setComments(data), [data]);
    return { comments: apiResult.data, apiResult, getComments, setComments };
};

/* eslint-disable no-console */
console.log.bind(null) ;
console.warn.bind(null) ;
console.error.bind(null) ;

const useCommentPoster = () => {
    const [apiResult, getData] = useApi(null);
    const postComment = (comment, moduleType, courseSlug, moduleSlug, config) => getData({
        ...(config ?? {}),
        data: comment,
        url: API.comments(courseSlug, moduleType, moduleSlug),
        method: "POST"
    });
    return { response: apiResult.data, apiResult, postComment };
};

const CommentsSection = ({ data, courseSlug, moduleSlug, moduleType, actionOverridePostComment, ...props }) => {
    const { comments, getComments } = useComments(courseSlug, moduleType, moduleSlug, data);
    const { postComment, apiResult: postCommentResponse } = useCommentPoster();
    useEffect(() => {
        if (!postCommentResponse.isLoading) {
            getComments(courseSlug, moduleType, moduleSlug, { cache: false });
        }
    }, [postCommentResponse, courseSlug, moduleType, moduleSlug]);
    const handlePostComment = useCallback((comment) => {
        actionOverridePostComment ? actionOverridePostComment(comment)
            : (courseSlug && moduleSlug && postComment(comment, moduleType, courseSlug, moduleSlug));
    }, [actionOverridePostComment, moduleSlug, courseSlug]);
    return (jsxs(Box, { ...props, children: [jsx(CommentInput, { postComment: handlePostComment }), comments?.map(comment => jsx(CommentThread, { comment: comment, postComment: handlePostComment }, comment.id))] }));
};

export { CommentCard, CommentInput, CommentThread, CommentsSection };
